import {_utils} from "./_utils.js";
import {OverlayScrollbars} from 'overlayscrollbars';
import Swiper from "swiper";
import {Keyboard} from "swiper/modules";
import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger.js";
import 'paginationjs';
import {_state} from "../state/state.js";
import 'lazysizes';

import Splitting from 'splitting';
import AOS from "aos";
import TomSelect from "tom-select";
import Lenis from 'lenis';
import Lottie from 'lottie-web';
import 'fslightbox';
import Ukiyo from "ukiyojs";
import Marquee3k from 'marquee3000';


Swiper.use([Keyboard]);
gsap.registerPlugin(ScrollTrigger);
gsap.ticker.fps(60);

export const _libs = {
    init() {
        return new Promise(async (resolve, reject) => {
            await Promise.all([
                this._initCustomSelect(),
                this._initAOS(),
                this._initSplitting(),
                this._lenis(),
                this._lottie(),
                this._ukiyo(),
                this._marquee(),
            ]);
            resolve();
        });
    },
    initOnUserInteraction() {
        return new Promise(async (resolve, reject) => {
            await Promise.all([
                this._initSplitting(false),
            ]);
            resolve();
        });
    },

    _lazyLoad() {
        return new Promise((resolve) => {
            const prepareLazyLoad = (elements, srcAttr = 'src', srcSetAttr = 'srcset') => {
                elements.forEach((el) => {
                    el.setAttribute('data-expand', `${window.innerHeight * 0.5}`);
                });
            };
            const images = document.querySelectorAll('img[data-src], img[data-srcset]');
            prepareLazyLoad(images);

            const videos = document.querySelectorAll('video');
            videos.forEach((video) => {
                const sources = video.querySelectorAll('source[data-src]');
                if (video.hasAttribute('data-src')) {
                    let once = true;
                    const src = video.getAttribute('data-src');
                    video.addEventListener('play', () => {
                        if (once) {
                            once = false;
                            video.setAttribute('src', src);
                            video.removeAttribute('data-src');
                        }
                    })
                } else if (sources.length > 0) {
                    let once = true;
                    video.addEventListener('play', () => {
                        if (once) {
                            once = false;
                            sources.forEach(source => {
                                source.setAttribute('src', source.getAttribute('data-src'));
                                source.removeAttribute('data-src');
                            })
                            video.load();
                        }
                    })
                }
            });

            resolve();
        })
    },

    _OverlayScrollbars() {
        return new Promise((resolve, reject) => {
            if (!_utils.isMobile.any()) {
                document.querySelectorAll('[data-os]').forEach(el => {
                    OverlayScrollbars(el, {
                        scrollbars: {}
                    });
                });
            }
            resolve();
        })
    },
    _initCustomSelect() {
        return new Promise((resolve) => {
            const selects = document.querySelectorAll('select:not(.js-handled)');
            if (selects.length > 0) {
                selects.forEach(select => {
                    select.classList.add('js-handled');
                    _libs._tomSelect(select);
                })
            }

            resolve();
        })
    },

    _initSplitting(isInitial = true) {
        return new Promise((resolve) => {
            const elements = document.querySelectorAll('[data-splitting]:not(.splitting)');
            elements.forEach((target, targetIndex) => {
                if (isInitial) {
                    if (target.getBoundingClientRect().top - window.innerHeight <= 0 && !_utils.isElementHidden(target)) {
                        Splitting({
                            target,
                            by: target.getAttribute('data-splitting').length > 0 ? target.getAttribute('data-splitting') : 'chars'
                        });
                    } else if (target.closest('[data-section]') && target.closest('[data-section]').getBoundingClientRect().top - window.innerHeight <= 0) {
                        Splitting({
                            target,
                            by: target.getAttribute('data-splitting').length > 0 ? target.getAttribute('data-splitting') : 'chars'
                        });
                    }
                } else {
                    Splitting({
                        target,
                        by: target.getAttribute('data-splitting').length > 0 ? target.getAttribute('data-splitting') : 'chars'
                    });
                }
            });
            resolve();
        });
    },
    _initAOS() {
        return new Promise((resolve) => {
            AOS.init({
                duration: 800,
                once: true,
                offset: window.innerHeight / 10,
            });
            _utils.rebuildComponentsOnResize(() => {
                AOS.refresh();
            })

            resolve();
        })
    },
    _tomSelect(select) {
        return new TomSelect(select, {
            create: false, // Allows you to enter your own values
            sortField: null, // Sorting
            maxItems: 1, // Number of selected items
            hideSelected: false, // Hide selected
            allowEmptyOption: true, // Prohibit empty value

            controlInput: false,

            closeAfterSelect: true, // Close the list after selecting an item
            selectOnTab: true, // Select an item by pressing Tab
            maxOptions: select.options.length
        });
    },
    _lenis() {
        return new Promise((resolve, reject) => {
            if(!_utils.isMobile.any()){
                const lenis = new Lenis({
                    smoothWheel: true,
                    smoothTouch: true,
                    duration: _utils.detectBrowser() === 'safari' ? 1 : 1.25,
                    wheelMultiplier: 1,
                    touchMultiplier: 1,
                    lerp: _utils.detectBrowser() === 'safari' ? 0.1 : 0.075,
                    easing: (t) => 1 - Math.pow(1 - t, 3),
                });

                function raf(time) {
                    lenis.raf(time);
                    requestAnimationFrame(raf);
                }

                requestAnimationFrame(raf);
                _state.updateState({lenisInit: lenis});
            }
            resolve();
        });
    },

    _createAnim(params) {
        return Lottie.loadAnimation(params);
    },
    _lottieCreate(block, closest) {
        let lottieAnimation = null;

        const animPath = block.hasAttribute('data-anim-path') && block.getAttribute('data-anim-path').length > 0
            ? block.getAttribute('data-anim-path') : null;
        const animSpeed = block.hasAttribute('data-anim-speed') && block.getAttribute('data-anim-speed').length > 0
            ? block.getAttribute('data-anim-speed') : null;
        const animLoop = block.hasAttribute('data-anim-loop');
        const animLoopReverse = block.hasAttribute('data-anim-loop-reverse');
        const imagesPreview = block.querySelectorAll('img');


        const animParams = {
            container: block,
            renderer: 'canvas', //svg/canvas/html
            loop: animLoop,
            autoplay: closest && closest.classList.contains('s-visible'),
        }
        if (animPath) {
            animParams.path = animPath;
        }

        if ('path' in animParams) {
            try {
                lottieAnimation = _libs._createAnim(animParams);
                if (animSpeed) {
                    lottieAnimation.setSpeed(animSpeed);
                }
                lottieAnimation.addEventListener('DOMLoaded', function () {
                    imagesPreview.forEach(img => {
                        if (img) {
                            img?.remove();
                        }
                    });
                });

            } catch (error) {
            }
        }

        if (lottieAnimation && animLoopReverse) {
            let direction = 1;

            lottieAnimation.addEventListener('complete', function () {
                direction *= -1;
                lottieAnimation.setDirection(direction);
                lottieAnimation.play();
            });
        }
        if (lottieAnimation && closest) {
            closest.addEventListener('sVisible', () => {
                lottieAnimation.play();
            })
            closest.addEventListener('sHidden', () => {
                lottieAnimation.pause();
            })
        }
    },
    _lottie() {
        return new Promise((resolve, reject) => {
            const blocks = document.querySelectorAll('.lottie');
            if (blocks.length > 0) {
                blocks.forEach(block => {
                    const closest = block.closest('[data-section]');
                    if (closest) {
                        let initOnce = false;
                        closest.addEventListener('sVisible', () => {
                            if (!initOnce) {
                                initOnce = true;
                                _libs._lottieCreate(block, closest);
                            }
                        })
                    }
                })
            }
            resolve();
        })
    },

    _ukiyo() {
        return new Promise((resolve, reject) => {
            const images = document.querySelectorAll('.ukiyo');
            let ukiyoImages = document.querySelectorAll('.ukiyo:not(.lazyload)');

            const initUkiyo = () => {
                new Ukiyo('.ukiyo:not(.lazyload,.js-handled)', {
                    scale: 1.2, // 1~2 is recommended
                });
                document.querySelectorAll('.ukiyo:not(.lazyload,.js-handled)').forEach((item) => {
                    item.classList.add('js-handled');
                })
            }

            initUkiyo();

            images.forEach(img => {
                img.onload = () => {
                    ukiyoImages = document.querySelectorAll('.ukiyo:not(.lazyload)');
                    initUkiyo();
                }
            })

            resolve();
        })
    },
    _marquee() {
        return new Promise((resolve, reject) => {
            const marquee = document.querySelectorAll('.marquee');
            if (marquee.length > 0) {
                marquee.forEach((el, elIndex) => {
                    if (!el.hasAttribute('data-speed')) el.setAttribute('data-speed', '1.5'); // play around here
                    //data-reverse="true"
                    //data-pausable
                })

                Marquee3k.init({
                    selector: 'marquee',
                });

                marquee.forEach((el, elIndex) => {
                    const closest = el.closest('[data-section]');
                    if (closest) {
                        if (!closest.classList.contains('s-visible')) Marquee3k.pause(elIndex);

                        closest.addEventListener('sVisible', () => {
                            Marquee3k.play(elIndex);
                        })
                        closest.addEventListener('sHidden', () => {
                            Marquee3k.pause(elIndex);
                        })
                    }
                })

                _utils.rebuildComponentsOnResize(() => {
                    Marquee3k.refreshAll();
                });
            }
            resolve();
        })
    },

    _swiper(container, options) {
        const swiper = new Swiper(container, options);

        _utils.rebuildComponentsOnResize(() => {
            if (swiper) swiper.update();
        });

        return swiper;
    },
    _gsap(options = {}) {
        const tl = gsap.timeline({
            scrollTrigger: options,
        });

        _utils.rebuildComponentsOnResize(() => {
            if (tl && tl.scrollTrigger) tl.scrollTrigger.refresh();
        });
        return tl;
    },
    _paginationJs(element = null, maxNumPages, pageNumber, pageSize, callbackFunc) {
        if (maxNumPages > 1 && element !== null) {
            let isInitialized = false;
            $(element).removeClass('hide');
            $(element).pagination({
                dataSource: Array.from({length: maxNumPages}, (_, i) => i + 1),
                pageSize: 1,
                itemsPerPage: pageSize,
                pageNumber: pageNumber,
                totalItems: pageSize * maxNumPages,
                showPrevious: true,
                showNext: true,
                showPageNumbers: true,
                useBootstrap: false,
                hideWhenLessThanOnePage: true,
                triggerPagingOnInit: true,
                pageRange: maxNumPages,
                callback: function (data, pagination) {
                    $(element).find('[title]').removeAttr('title');
                    if (!isInitialized) {
                        isInitialized = true;
                        return;
                    }

                    if (callbackFunc && typeof callbackFunc === 'function') {
                        callbackFunc(data, pagination);
                    }
                }
            });

            return {
                destroy: function () {
                    $(element).pagination('destroy');
                }
            };
        } else {
            if (element) $(element).addClass('hide');
            return null;
        }
    },
    _refreshFsLightbox() {
        return new Promise((resolve, reject) => {
            if (typeof refreshFsLightbox === 'function') {
                refreshFsLightbox();
            }
            resolve();
        })
    },
}